import React from 'react';
import { useLabels, usePrefs } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { templatePropTypes } from '../../utilities/commonPropTypes';
import AspectRatioBox from '../../components/aspect-ratio-box/AspectRatioBox';
import OptimizedImageBackground from '../../components/OptimizedImageBackground';
import {
	ctaButtonFragment,
	detailsButtonFragment,
	offerDetailsFragment
} from '../commonFragments';
import {
	getVehicleImages,
	getIncentiveImage,
	getNoImageLabels,
	getFormattedDollarSign
} from '../../utilities/general.jsx';
import { g } from '../../global-constants';
import './sass/Desktop4x1.scss';

const Desktop4x1 = ({
	applicableVehicle = '',
	carouselPosition,
	couponCode = '',
	ctaButtonData,
	description,

	discountDisplay = '',
	imageUrl,
	incentive,
	isMobile,
	isVisible,
	offerDetails,
	promo,
	title,
	type,
	vehicleImages = [],
	width
}) => {
	let template;
	const labels = useLabels();
	const {
		reduceDollarSignSize,
		vehicleBackgroundImage,
		modelOfferBackgroundImage
	} = usePrefs();
	const ctaButtonClasses = setClassNames(['btn-inverse', 'd-table', 'mb-3']);
	const ctaModalButtonClasses = setClassNames(['my-4']);

	const detailsButtonClasses = [
		'pl-0',
		'text-decoration-underline',
		'text-transform-none'
	];

	const noImageLabels = getNoImageLabels(labels, promo.inventoryCount);
	const gradient =
		'linear-gradient(90deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.00) 40%, rgba(0, 0, 0, 0.70) 50%, rgba(0, 0, 0, 0.70) 100%)';

	if ([g.PARTS, g.SERVICE, g.EVENT].includes(type)) {
		template = (
			<div className="d-flex flex-row">
				<OptimizedImageBackground
					key={type}
					className="flex-1 w-100 flex-shrink-0 image"
					style={{
						backgroundImage: `url("${imageUrl}")`,
						backgroundPosition: 'center',
						backgroundSize: 'cover',
						flexBasis: `${width / 2}px`,
						backgroundColor: 'black'
					}}
					width={width / 2}
				/>
				<div
					className={setClassNames([
						'flex-1',
						'd-flex',
						'flex-column',
						'justify-content-center',
						'flex-shrink-0',
						'align-middle',
						'text'
					])}
					style={{
						flexBasis: `${width / 2}px`,
						paddingRight: '5%',
						paddingLeft: '3%'
					}}
				>
					{offerDetailsFragment({
						couponCode,
						cta: ctaButtonFragment({
							carouselPosition,
							ctaButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						}),
						description: getFormattedDollarSign(
							description,
							reduceDollarSignSize
						),
						details: detailsButtonFragment({
							carouselPosition,
							detailsButtonClasses,

							isVisible,
							promo,
							cta: ctaButtonFragment({
								carouselPosition,
								ctaModalButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})
						}),
						discountDisplay,
						labels,
						title: getFormattedDollarSign(
							title,
							reduceDollarSignSize
						)
					})}
				</div>
			</div>
		);
	}

	if (type && type === g.VEHICLE) {
		template = (
			<OptimizedImageBackground
				className="d-flex flex-row"
				style={{
					backgroundImage: `${gradient},url("${vehicleBackgroundImage}")`,
					backgroundPosition: 'center',
					backgroundSize: 'cover',
					flexBasis: `${width}px`
				}}
				width={width}
			>
				<div
					key={type}
					className={setClassNames([
						'flex-1',
						'd-flex',
						'flex-row',
						'justify-content-center',
						'flex-shrink-0',
						'align-middle'
					])}
					style={{
						flexBasis: `${width / 2}px`,
						alignItems: 'center',
						paddingLeft: '1%',
						paddingRight: '1%'
					}}
				>
					{getVehicleImages(vehicleImages, width / 2, isMobile, 0, {
						title: noImageLabels.title,
						subtitle: noImageLabels.subtitle
					})}
				</div>
				<div
					className={setClassNames([
						'flex-1',
						'd-flex',
						'flex-column',
						'justify-content-center',
						'flex-shrink-0',
						'align-middle'
					])}
					style={{
						flexBasis: `${width / 2}px`,
						paddingRight: '5%',
						paddingLeft: '3%'
					}}
				>
					{offerDetailsFragment({
						couponCode,
						cta: ctaButtonFragment({
							carouselPosition,
							ctaButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						}),
						description: getFormattedDollarSign(
							description,
							reduceDollarSignSize
						),
						details: detailsButtonFragment({
							carouselPosition,
							detailsButtonClasses,

							isVisible,
							promo,
							cta: ctaButtonFragment({
								carouselPosition,
								ctaModalButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})
						}),
						discountDisplay,
						labels,
						title: getFormattedDollarSign(
							title,
							reduceDollarSignSize
						)
					})}
				</div>
			</OptimizedImageBackground>
		);
	}

	if (type && type === g.INCENTIVE) {
		template = (
			<OptimizedImageBackground
				key={type}
				className="d-flex flex-row"
				style={{
					backgroundImage: `${gradient},url("${modelOfferBackgroundImage}")`,
					backgroundPosition: 'bottom',
					backgroundSize: 'cover',
					flexBasis: `${width}px`
				}}
				width={width}
			>
				<div
					key={type}
					className="flex-1 d-flex flex-row justify-content-center flex-shrink-0 align-middle"
					style={{
						flexBasis: `${width / 2}px`,
						alignItems: 'center'
					}}
				>
					{getIncentiveImage(
						imageUrl,
						width / 2,
						'', // leave empty unless we have a label other than YMMT
						isMobile,
						65,
						{
							title: noImageLabels.title,
							subtitle: noImageLabels.subtitle
						}
					)}
				</div>
				<div
					className="flex-1 d-flex flex-column justify-content-center flex-shrink-0 align-middle"
					style={{
						flexBasis: `${width / 2}px`,
						paddingRight: '5%',
						paddingLeft: '3%'
					}}
				>
					<h2>
						<div className="pb-3">{applicableVehicle}</div>
						<div className="font-weight-normal">
							{getFormattedDollarSign(
								title,
								reduceDollarSignSize
							)}
						</div>
					</h2>
					<p>
						{getFormattedDollarSign(
							offerDetails,
							reduceDollarSignSize
						)}
					</p>
					<div className="cta-wrapper">
						{ctaButtonFragment({
							carouselPosition,
							ctaButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						})}
						{detailsButtonFragment({
							carouselPosition,
							detailsButtonClasses,

							incentive,
							isVisible,
							promo,
							cta: ctaButtonFragment({
								carouselPosition,
								ctaModalButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})
						})}
					</div>
				</div>
			</OptimizedImageBackground>
		);
	}

	return (
		<AspectRatioBox>
			{template}
		</AspectRatioBox>
	);
};

Desktop4x1.propTypes = templatePropTypes;

export default Desktop4x1;
